import { Database, HardDrive } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { ConnectionDebugInfo } from '@/hooks/useDocuments';
import { BRAND_NAVY } from '@/constants/brandColors';

interface ConnectionStatusIndicatorProps {
  isConnected: boolean;
  debugInfo?: ConnectionDebugInfo;
}

export function ConnectionStatusIndicator({ isConnected, debugInfo }: ConnectionStatusIndicatorProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="flex items-center gap-1 mr-1 sm:mr-2 text-xs transition-colors cursor-pointer bg-transparent border-none outline-none"
          style={{ color: BRAND_NAVY }}
          title={isConnected ? 'Connected to database' : 'Using local storage'}
          aria-label={isConnected ? 'Connected to database' : 'Using local storage'}
          data-testid="connection-status"
        >
          {isConnected ? <Database className="w-3.5 h-3.5" aria-hidden="true" /> : <HardDrive className="w-3.5 h-3.5" aria-hidden="true" />}
          <span className="hidden sm:inline">{isConnected ? 'DB' : 'Local'}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-xs" align="end">
        <div className="space-y-2">
          <div className="font-semibold text-sm flex items-center gap-2">
            {isConnected ? (
              <>
                <div className="w-2 h-2 rounded-full bg-green-500" aria-hidden="true" />
                <span aria-live="polite">Database Connected</span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 rounded-full bg-yellow-500" aria-hidden="true" />
                <span aria-live="polite">Local Storage Mode</span>
              </>
            )}
          </div>

          {debugInfo && (
            <div className="space-y-1.5 pt-2 border-t text-muted-foreground">
              <Row label="Embedded in iframe" value={debugInfo.isIframe ? 'Yes' : 'No'} ok={debugInfo.isIframe} />
              <Row label="Credentials received" value={debugInfo.credentialsReceived ? 'Yes' : 'No'} ok={debugInfo.credentialsReceived} />
              <Row label="Access token" value={debugInfo.hasAccessToken ? 'Present' : 'Missing'} ok={debugInfo.hasAccessToken} />
              {debugInfo.lastMessageOrigin && (
                <Row label="Last message origin" value={debugInfo.lastMessageOrigin} />
              )}
              {debugInfo.lastMessageType && (
                <Row label="Last message type" value={debugInfo.lastMessageType} />
              )}
              {debugInfo.rejectedOrigins.length > 0 && (
                <div className="text-destructive">
                  <span className="font-medium">Rejected origins:</span>
                  <ul className="ml-3 list-disc">
                    {debugInfo.rejectedOrigins.slice(-5).map((o, i) => (
                      <li key={i} className="break-all">{o}</li>
                    ))}
                  </ul>
                </div>
              )}
              {debugInfo.error && (
                <div className="text-destructive">
                  <span className="font-medium">Error:</span> {debugInfo.error}
                </div>
              )}
              {!debugInfo.isIframe && (
                <p className="text-muted-foreground/80 italic pt-1">
                  Database connection requires embedding this app in an iframe and sending credentials via postMessage.
                </p>
              )}
              {debugInfo.isIframe && !debugInfo.credentialsReceived && (
                <p className="text-muted-foreground/80 italic pt-1">
                  Waiting for parent window to send "supabase-credentials" message. Make sure the host app sends credentials after the iframe loads.
                </p>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Row({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <span>{label}</span>
      <span className={`font-mono ${ok === true ? 'text-green-600' : ok === false ? 'text-yellow-600' : ''}`}>
        {value}
      </span>
    </div>
  );
}
